import { useAuth } from "../contexts/AuthContext";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSystemApi } from "../apis/system-apis";
import { Alert, AlertDescription } from "./ui/Alert";
import { Loader2 } from "lucide-react";
import AgenticGenieLogo from '../assets/AgenticGenie.png';

const OAuthCallback = () => {
  const { handleOAuthCallback, isAuthenticated, isLoading, setSystemInfo } = useAuth();
  const { getSystemInfo } = useSystemApi();
  const navigate = useNavigate();
  const [processingAuth, setProcessingAuth] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingStep, setProcessingStep] = useState<string>("Verifying your credentials...");

  useEffect(() => {
    const processAuth = async () => {
      console.log('Starting OAuth callback processing');
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const error = params.get('error');
      const errorDescription = params.get('error_description');
      
      if (error || errorDescription) {
        console.error('OAuth error:', error, errorDescription);
        setError(errorDescription || 'Authentication failed. Please try again.');
        setProcessingAuth(false);
        setTimeout(() => navigate('/marketplace/login', { replace: true }), 3000);
        return;
      }
      
      if (code) {
        try {
          console.log('Code found, calling handleOAuthCallback');
          setProcessingAuth(true);
          setProcessingStep("Verifying your credentials...");
          await handleOAuthCallback(code);

          setProcessingStep("Retrieving your account information...");
          try {
            console.log('Fetching system info after OAuth callback');
            const systemInfo = await getSystemInfo();
            console.log('System info received:', systemInfo);
            
            // Set system info with the enhanced function that also persists to localStorage
            setSystemInfo(systemInfo);
            
            // Explicitly store in localStorage as a backup
            if (systemInfo) {
              localStorage.setItem('systemInfo', JSON.stringify(systemInfo));
            }
          } catch (error) {
            console.error('Error fetching system info:', error);
            
            // Try to retrieve cached system info if available
            setProcessingStep("Using cached account information...");
            const cachedSystemInfo = localStorage.getItem('systemInfo');
            if (cachedSystemInfo) {
              try {
                const parsedSystemInfo = JSON.parse(cachedSystemInfo);
                setSystemInfo(parsedSystemInfo);
                console.log('Using cached system info:', parsedSystemInfo);
              } catch (parseError) {
                console.error('Error parsing cached system info:', parseError);
              }
            }
          }
          
          // Wait for state updates
          setProcessingStep("Finalizing your sign-in...");
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          const accessToken = localStorage.getItem('accessToken');
          if (accessToken) {
            console.log('Access token found, navigating to /skills');
            setProcessingStep("Sign-in successful! Redirecting...");
            navigate('/marketplace/skills', { replace: true });
          } else {
            console.log('No access token found, navigating to /login');
            setError('Authentication failed. Please try again.');
            navigate('/marketplace/login', { replace: true });
          }
        } catch (error: any) {
          console.error('OAuth callback error:', error);
          let errorMessage = 'An error occurred during sign in. Please try again.';
          
          // Handle disabled user error
          if (error.message?.includes('disabled') || error.message?.includes('User is disabled')) {
            errorMessage = 'Your account has been disabled. Please contact support.';
          }
          
          setError(errorMessage);
          setTimeout(() => navigate('/marketplace/login', { replace: true }), 3000);
        } finally {
          setProcessingAuth(false);
        }
      } else {
        console.log('No code found in URL');
        setError('Invalid authentication request. Please try again.');
        setProcessingAuth(false);
        setTimeout(() => navigate('/marketplace/login', { replace: true }), 3000);
      }
    };

    processAuth();
  }, []);

  if (processingAuth || isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="w-full max-w-md p-8 bg-white rounded-lg shadow-md">
          <div className="flex flex-col items-center space-y-6">
            <img src={AgenticGenieLogo} alt="Agentic Genie" className="h-40 w-auto" />
            
            <div className="flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full">
              <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
            </div>
            
            <div className="space-y-2 text-center">
              <h3 className="text-xl font-medium text-gray-900">Signing you in</h3>
              <p className="text-sm text-gray-500">{processingStep}</p>
            </div>
            
            {/* Progress bar */}
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div className="bg-blue-600 h-2.5 rounded-full animate-pulse w-full"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-full max-w-md p-8 bg-white rounded-lg shadow-md">
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <div className="mt-4 text-center text-sm text-gray-600">
            Redirecting to login page...
          </div>
          <div className="mt-8 text-center">
            <a 
              href="/marketplace/login" 
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Return to login
            </a>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default OAuthCallback;